// 모험 목표판 장부(레인 E 2단계, 새 키 dungeonhero.adventure.v1 — 캐릭터별 진행 키, account/keys.ts SYNC_KEYS).
// 형식 { v:1, done:[현상금 id], found:[이름 있는 유니크 id] }. 쓰기 전에 다시 읽어 다른 탭 변경을 합친다(합집합).
// 읽기 예외는 덮어쓰지 않고, 미래 판본(v > 1)은 읽기만 한다(AvatarStore와 같은 보존 규칙). 이전 빌드는 이 키를 모른다(롤백 안전).
import { CODEX_STEPS } from "../shared/adventure";
import { dayIndex } from "../shared/daily";
import type { SaveStorage } from "./save";

export const ADVENTURE_KEY = "dungeonhero.adventure.v1";
/** 기억하는 완료 현상금 수(하루 3개 × 약 20일) */
export const ADVENTURE_DONE_CAP = 60;
const ID_RE = /^[0-9]{1,6}:[a-z0-9_-]{1,24}$/;
const NU_RE = /^nu_[a-z0-9_]{1,40}$/;

/** seen = 본 가장 늦은 시각(ms). 시계를 거꾸로 돌려도 날짜가 되돌아가지 않게(shared/daily.ts seenMs와 같은 방어) */
interface AdventureData { done: string[]; found: string[]; seen: number; claimed: number[] }

export class AdventureStore {
  private data: AdventureData = { done: [], found: [], seen: 0, claimed: [] };
  private readonly_ = false;
  /** 바뀔 때마다 1 오른다(UI 다시 그리기) */
  revision = 0;

  constructor(private readonly storage: SaveStorage) { this.read(); }

  get done(): readonly string[] { return this.data.done; }
  get found(): readonly string[] { return this.data.found; }
  /** 받은 도감 단계(5·10·20) */
  get claimed(): readonly number[] { return this.data.claimed; }
  /** 마지막으로 받은 도감 단계(결과 띠) */
  lastClaim: { step: number; serial: number } | null = null;
  private claimSerial = 0;
  isDone(id: string): boolean { return this.data.done.includes(id); }

  read(): void {
    let raw: string | null;
    try { raw = this.storage.getItem(ADVENTURE_KEY); } catch { this.readonly_ = true; return; }
    const p = raw ? parse(raw) : null;
    // 키는 있는데 깨졌으면 원문을 .bak에 남기고 빈 장부로 시작한다(완료 목록·도감 원문을 조용히 잃지 않게)
    if (raw && !p) { try { this.storage.setItem(ADVENTURE_KEY + ".bak", raw); } catch { this.readonly_ = true; return; } }
    this.readonly_ = p?.future === true;
    this.data = p ? p.data : { done: [], found: [], seen: 0, claimed: [] };
    this.revision++;
  }

  private write(): boolean {
    if (this.readonly_) return false;
    try { this.storage.setItem(ADVENTURE_KEY, JSON.stringify({ v: 1, done: this.data.done, found: this.data.found, seen: this.data.seen, claimed: this.data.claimed })); return true; }
    catch { return false; }
  }

  /** 현상금 날짜: 본 가장 늦은 시각보다 과거로는 가지 않는다 */
  dayAt(nowMs: number): number { return dayIndex(Math.max(nowMs, this.data.seen)); }

  /** 현상금 완료 표시. 새로 완료했으면 true(이미 완료·쓰기 실패면 false — 추가 득템 판정은 true일 때만) */
  complete(id: string, nowMs = 0): boolean {
    if (!ID_RE.test(id)) return false;
    this.read();
    if (this.data.done.includes(id)) return false;
    const before = this.data.done.slice(), seenBefore = this.data.seen;
    this.data.done.push(id);
    if (nowMs > this.data.seen) this.data.seen = nowMs;
    if (this.data.done.length > ADVENTURE_DONE_CAP) this.data.done.splice(0, this.data.done.length - ADVENTURE_DONE_CAP);
    if (!this.write()) { this.data.done = before; this.data.seen = seenBefore; return false; }
    this.revision++;
    return true;
  }

  /**
   * 도감 단계 받음 표시. 새로 표시했으면 true(이미 받음·쓰기 실패면 false). 다시 읽고 합쳐 쓰므로 두 탭이 같은 단계를 두 번 받지 못한다.
   * 보상 지급(보석 연산 id·아바타 opId)이 단계별 고정 id라 지급 뒤 이 표시 전에 끊겨도 다시 받기가 한 번만 지급한다
   */
  claim(step: number): boolean {
    if (!(CODEX_STEPS as readonly number[]).includes(step)) return false;
    this.read();
    if (this.readonly_ || this.data.claimed.includes(step)) return false;
    const before = this.data.claimed.slice();
    this.data.claimed = [...before, step].sort((a, b) => a - b);
    if (!this.write()) { this.data.claimed = before; return false; }
    this.lastClaim = { step, serial: ++this.claimSerial };
    this.revision++;
    return true;
  }

  /** 도감: 얻은 이름 있는 유니크를 기록한다(팔거나 분해해도 도감에는 남는다). 새로 기록한 것이 있을 때만 쓴다 */
  noteFound(ids: readonly string[]): void {
    const fresh = ids.filter((id) => NU_RE.test(id) && !this.data.found.includes(id));
    if (fresh.length === 0) return;
    this.read();
    const add = [...new Set(fresh)].filter((id) => !this.data.found.includes(id));
    if (add.length === 0) return;
    const before = this.data.found.slice();
    this.data.found.push(...add);
    if (!this.write()) { this.data.found = before; return; }
    this.revision++;
  }
}

function parse(raw: string): { data: AdventureData; future: boolean } | null {
  try {
    const v = JSON.parse(raw) as Record<string, unknown>;
    if (!v || typeof v.v !== "number" || v.v < 1) return null;
    const done = Array.isArray(v.done) ? [...new Set((v.done as unknown[]).filter((x): x is string => typeof x === "string" && ID_RE.test(x)))].slice(-ADVENTURE_DONE_CAP) : [];
    const found = Array.isArray(v.found) ? [...new Set((v.found as unknown[]).filter((x): x is string => typeof x === "string" && NU_RE.test(x)))] : [];
    const seen = typeof v.seen === "number" && Number.isFinite(v.seen) && v.seen > 0 ? v.seen : 0;
    const claimed = Array.isArray(v.claimed) ? [...new Set((v.claimed as unknown[]).filter((x): x is number => typeof x === "number" && (CODEX_STEPS as readonly number[]).includes(x)))].sort((a, b) => a - b) : [];
    return { data: { done, found, seen, claimed }, future: v.v > 1 };
  } catch { return null; }
}
